import React, { useContext } from "react";
import FormikField from "components/forms/formik-field";
import { Form, Formik, FieldArray } from "formik";
import { selectors } from "reducers";
import { connect } from "react-redux";
import MdCancel from "react-icons/lib/md/cancel";
import MdAdd from "react-icons/lib/md/add";
import { IconButton } from "components/ui";
import PropTypes from "prop-types";
import * as Yup from "yup";
import IoIosInformationCircleOutline from "react-icons/lib/io/ios-information";
import { Tooltip } from "components/ui/tool-tip/tooltip";

import { InsuranceCompanyContext } from "./insurance-company-provider";
import {
  PER_LINE_SETTINGS,
  isSettingApplicableToLine
} from "./insurance-product-line-settings";

// S3DEV2-7333: allowable duration is entered in DAYS. Returns a non-blocking hint when a value
// looks like a leftover month count (<= 12) or isn't a whole 30-day cycle, to steer users away
// from entering months out of habit. This only warns; it never blocks submit.
const getDayValueHint = value => {
  if (value === "" || value === null || value === undefined) return null;
  const days = Number(value);
  if (Number.isNaN(days) || days <= 0) return null;
  if (days <= 12 || days % 30 !== 0) {
    return "Entered in days (e.g., 90). This looks unusual — double-check it's days, not months.";
  }
  return null;
};

// Coerce a number-like form value into the backend's int? contract. Empty/undefined → null;
// other values → Number so the JSON leaves the wire as an int not a numeric string.
const toNullableInt = value =>
  value === "" || value === null || value === undefined ? null : Number(value);

// Strip the FE-only productLineName from each productLines[] row and coerce numeric inputs
// before sending to backend. Formik keeps productLineName in state so the table header can
// label the column, but the wire payload only carries the fields the backend contract expects.
const cleanUpdateInsurancePayload = formData => ({
  ...formData,
  productLines: formData.productLines.map(line => ({
    fkiProductLine: Number(line.fkiProductLine),
    iAllowableDuration: Number(line.iAllowableDuration) || 0,
    bAllowsNinetyDaySupply: !!line.bAllowsNinetyDaySupply,
    iCapitated: toNullableInt(line.iCapitated),
    bRequiresCompliance: !!line.bRequiresCompliance,
    bCountsMaskAsSeal: !!line.bCountsMaskAsSeal,
    bCMNRequired: !!line.bCMNRequired
  }))
});

// Small tooltip attached next to a setting label. Only shown for settings with tooltip copy.
const SettingTooltip = ({ id, message }) => (
  <Tooltip id={id} effect="solid" message={message}>
    <IoIosInformationCircleOutline size="1rem" />
  </Tooltip>
);
SettingTooltip.propTypes = {
  id: PropTypes.string,
  message: PropTypes.string
};

// One cell in the Product Line Settings table. Booleans render as a checkBox FormikField;
// numeric fields as a small number input. Cells that don't apply to a given line (per
// isSettingApplicableToLine) render as "N/A" text — Formik state stays at whatever the
// initial value was, which nothing on the backend reads for these (line, setting) pairs.
const SettingCell = ({ setting, line, index }) => {
  if (!isSettingApplicableToLine(line.fkiProductLine, setting.key)) {
    return <span style={{ color: "#999" }}>N/A</span>;
  }
  const fieldName = `productLines[${index}].${setting.key}`;
  if (setting.type === "checkbox") {
    return (
      <FormikField
        name={fieldName}
        type="checkBox"
        checked={!!line[setting.key]}
        className="form-field inline-checkbox"
        style={{ display: "flex", justifyContent: "center" }}
      />
    );
  }
  return (
    <FormikField
      name={fieldName}
      type="number"
      style={{ width: 90 }}
    />
  );
};
SettingCell.propTypes = {
  setting: PropTypes.object,
  line: PropTypes.object,
  index: PropTypes.number
};

const ProductLineSettingsTable = ({ productLines }) => (
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th style={{ textAlign: "left", padding: 6 }}>Setting</th>
        {productLines.map(line => (
          <th
            key={line.fkiProductLine}
            style={{ textAlign: "left", padding: 6 }}
          >
            {line.productLineName}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {PER_LINE_SETTINGS.map(setting => (
        <tr key={setting.key}>
          <td style={{ padding: 6, whiteSpace: "nowrap" }}>
            {setting.label}
            {setting.unit && (
              <span style={{ color: "#666" }}> ({setting.unit})</span>
            )}
            {setting.key === "iAllowableDuration" && (
              <>
                {" "}
                <SettingTooltip
                  id="allowable-duration-tip"
                  message="Entered in days (e.g., 90 = a ~3-month cycle)."
                />
              </>
            )}
          </td>
          {productLines.map((line, index) => (
            <td
              key={line.fkiProductLine}
              style={{ padding: 6, verticalAlign: "middle" }}
            >
              <SettingCell setting={setting} line={line} index={index} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);
ProductLineSettingsTable.propTypes = {
  productLines: PropTypes.arrayOf(PropTypes.object)
};

const InsuranceCompanyForm = ({
  insuranceOptions,
  values,
  closeModal,
  add
}) => {
  const insuranceName = values.sInsuranceCompanyName;
  const { updateInsurance } = useContext(InsuranceCompanyContext);
  return (
    <Formik
      initialValues={values}
      onSubmit={formData => {
        updateInsurance(cleanUpdateInsurancePayload(formData));
        closeModal();
      }}
      validationSchema={Yup.object().shape({
        sInsuranceCompanyName: Yup.array()
          .of(Yup.string().required("Required"))
          .min(1),
        typeGuid: Yup.string().required("Required"),
        sPayorID: Yup.string().nullable(),
        sExtraInfo: Yup.string().nullable(),
        bActive: Yup.bool(),
        bExcluded: Yup.bool(),
        bUseAsPrimary: Yup.bool(),
        bUseAsSecondary: Yup.bool(),
        bEligibleForVerification: Yup.bool(),
        bAsksDysfunctionsQuestions: Yup.bool(),
        bIVRAutoShip: Yup.bool(),
        bIVREducationStatement: Yup.bool(),
        productLines: Yup.array().of(
          Yup.object().shape({
            fkiProductLine: Yup.number().integer(),
            iAllowableDuration: Yup.number().nullable().min(0),
            bAllowsNinetyDaySupply: Yup.bool(),
            iCapitated: Yup.number().nullable().min(0),
            bRequiresCompliance: Yup.bool(),
            bCountsMaskAsSeal: Yup.bool(),
            bCMNRequired: Yup.bool()
          })
        )
      })}
      enableReinitialize
    >
      {({ values, handleChange }) => {
        return (
          <div style={{ margin: "20px" }} id="insurance-company-form-container">
            <header
              className="page-header"
              style={{ margin: "0 10px 0 10px " }}
            >
              <div className="page-header-title">
                <h1>
                  <span className="page-title">
                    {add
                      ? "Insurance Company"
                      : values.prefix + "_" + insuranceName}
                  </span>
                </h1>
              </div>
            </header>
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                margin: 10
              }}
            >
              <fieldset>
                <legend>Main</legend>
                <FieldArray
                  name={"sInsuranceCompanyName"}
                  render={arrayHelpers => (
                    <>
                      <div
                        style={{
                          width: "100%",
                          display: "inline-flex",
                          justifyContent: "flex-end"
                        }}
                      >
                        {add ? (
                          <IconButton
                            type="button"
                            style={{
                              margin: "0 0 20px 0",
                              padding: "5px 15px",
                              float: "right"
                            }}
                            onClick={() => arrayHelpers.push("")}
                            text="Add"
                            icon={<MdAdd />}
                          />
                        ) : null}
                      </div>
                      {values.sInsuranceCompanyName?.map(
                        (sInsuranceCompanyName, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              justifyContent: "space-between"
                            }}
                          >
                            {!add && (
                              <div
                                style={{
                                  width: "25%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "flex-end",
                                  paddingBottom: "2.5%"
                                }}
                              >
                                {values.prefix + "_"}{" "}
                              </div>
                            )}
                            <FormikField
                              name={`sInsuranceCompanyName.${index}`}
                              label={`Name`}
                              style={{ width: "75%" }}
                              type="text"
                              onChange={handleChange}
                            />
                            {add ? (
                              <IconButton
                                onClick={() => arrayHelpers.remove(index)}
                                style={{
                                  padding: 3,
                                  height: "80%",
                                  alignSelf: "center"
                                }}
                                icon={<MdCancel />}
                                type="button"
                                text="Delete"
                              />
                            ) : null}
                          </div>
                        )
                      )}
                    </>
                  )}
                />
                <FormikField name="typeGuid" label="Type" component="select">
                  <option key={0} />
                  {insuranceOptions.map(io => {
                    return (
                      <option key={io.value} value={io.value}>
                        {io.text}
                      </option>
                    );
                  })}
                </FormikField>
              </fieldset>
              <fieldset>
                <legend>General Information</legend>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <FormikField
                    name="sPayorID"
                    label="Client Insurance ID"
                    disabled={values.sInsuranceCompanyName.length > 1}
                    style={{ width: "45%" }}
                    type="text"
                  />
                  <FormikField
                    name="sExtraInfo"
                    label="Extra Info"
                    style={{ width: "50%" }}
                    type="text"
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "10px"
                  }}
                >
                  <FormikField
                    name="bActive"
                    label="Active"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bActive}
                  />
                  <FormikField
                    name="bEligibleForVerification"
                    label="Eligible For Verification"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bEligibleForVerification}
                  />
                  <FormikField
                    name="bExcluded"
                    label="Excluded"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bExcluded}
                  />
                  <FormikField
                    name="bUseAsPrimary"
                    label="Use As Primary"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bUseAsPrimary}
                  />
                  <FormikField
                    name="bUseAsSecondary"
                    label="Use As Secondary"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bUseAsSecondary}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "25px"
                  }}
                >
                  <div>IVR:</div>
                  <FormikField
                    name="bAsksDysfunctionsQuestions"
                    label="Requires Dysfunctions Questions"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bAsksDysfunctionsQuestions}
                  />
                  <FormikField
                    name="bIVRAutoShip"
                    label="Offers Autoship"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bIVRAutoShip}
                  />
                  <FormikField
                    name="bIVREducationStatement"
                    label="Reads Education Statement"
                    type="checkBox"
                    style={{ display: "flex" }}
                    className="form-field inline-checkbox"
                    checked={values.bIVREducationStatement}
                  />
                </div>
              </fieldset>
              <fieldset>
                <legend>Product Line Settings</legend>
                <ProductLineSettingsTable productLines={values.productLines} />
                {values.productLines?.some(line =>
                  getDayValueHint(line.iAllowableDuration)
                ) && (
                  <div
                    style={{
                      color: "#a06b00",
                      fontSize: "0.8rem",
                      marginTop: "4px"
                    }}
                  >
                    {values.productLines.map(line => {
                      const hint = getDayValueHint(line.iAllowableDuration);
                      return hint ? (
                        <div key={line.fkiProductLine}>
                          Allowable Duration ({line.productLineName}): {hint}
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </fieldset>
              <div>
                <button type="submit" style={{ width: "12%", float: "right" }}>
                  Submit
                </button>
              </div>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};
export default connect(state => ({
  insuranceOptions: selectors.getFormOptionsInsurance(state)
}))(InsuranceCompanyForm);
InsuranceCompanyForm.propTypes = {
  insuranceOptions: PropTypes.arrayOf(PropTypes.object),
  closeModal: PropTypes.func,
  values: PropTypes.object,
  add: PropTypes.bool,
  prefix: PropTypes.string
};
